import React, { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import BASE_URL from "../utils/Urls";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const LivePayoutDetailPopup = ({ row, onClose }) => {
  const [summary, setSummary] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [companyHist, setCompanyHist] = useState({ total: 0, months: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sumRes, secRes, compRes] = await Promise.all([
          axios.get(`${BASE_URL}/dp-summary/${row.user_id}`).catch(() => null),
          axios.get(`${BASE_URL}/referral-sectors`).catch(() => null),
          axios.get(`${BASE_URL}/admin/company-credits/${row.user_id}`).catch(() => null),
        ]);
        if (!alive) return;
        setSummary(sumRes?.data?.summary || null);
        setSectors(secRes?.data?.sectors || []);
        setCompanyHist({
          total: Number(compRes?.data?.total) || 0,
          months: compRes?.data?.months || [],
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [row.user_id]);

  const coupons = summary?.coupons || {};
  const earnings = summary?.earnings || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start z-50 overflow-y-auto py-8">
      <div
        className="bg-white rounded-lg shadow-lg relative my-auto"
        style={{ width: "560px", maxHeight: "85vh", overflowY: "auto" }}
      >
        <button onClick={onClose} className="absolute top-3 right-5 text-red-600 hover:text-red-800 z-10">
          <IoClose className="text-2xl" />
        </button>
        <div className="px-6 pt-5 pb-3">
          <h2 className="text-center font-bold text-xl text-gray-800">{row.employer_name || row.user_id}</h2>
          <p className="text-center text-xs text-gray-400">
            Live payout detail · {row.main_employee_code || "not linked"}
          </p>
        </div>

        {loading ? (
          <div className="px-6 pb-8 text-center text-gray-400">Loading…</div>
        ) : !summary ? (
          <div className="px-6 pb-8 text-center text-gray-400">
            No main-app data (promoter not linked or integration off).
          </div>
        ) : (
          <div className="px-6 pb-6">
            <div className="flex gap-3 mb-3">
              <div className="flex-1 border rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-[#5907FF]">{summary.total_running_credits ?? 0}</div>
                <div className="text-xs text-gray-400">TOTAL CREDITS</div>
              </div>
              <div className="flex-1 border rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-[#29A80C]">{fmt(earnings.net)}</div>
                <div className="text-xs text-gray-400">SUPER BONUS (NET)</div>
              </div>
            </div>
            <div className="flex gap-3 mb-4 text-center text-xs">
              <div className="flex-1 text-[#29A80C]">Added {fmt(earnings.added)}</div>
              <div className="flex-1 text-[#FF0707]">Deducted {fmt(earnings.deducted)}</div>
            </div>

            <div className="font-bold text-sm text-gray-700 mb-2">Coupons</div>
            {["high", "low"].map((tier) => {
              const c = coupons[tier];
              if (!c) return null;
              return (
                <div
                  key={tier}
                  className={`border rounded-lg p-3 mb-2 ${tier === "high" ? "border-yellow-400" : "border-green-500"}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-mono font-bold">{c.code || "—"}</span>
                    <span className="text-xs text-gray-500">{tier.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>
                      Running credits: <b>{c.running_credits ?? 0}</b>
                    </span>
                    <span>
                      Unlocked accounts: <b>{c.unlocked_accounts ?? 0}</b>
                    </span>
                  </div>
                </div>
              );
            })}

            <div className="font-bold text-sm text-gray-700 mt-4 mb-2">Sector credits / discounts</div>
            <div className="max-h-64 overflow-y-auto">
              {sectors.map((s) => (
                <div key={s._id} className="flex justify-between items-center border-b py-2 text-sm">
                  <span className="text-gray-700">{s.name}</span>
                  <span className="text-xs">
                    <span className="text-green-600 font-bold">MIN {s.referral_credits_low ?? 0}</span>
                    {" · "}
                    <span className="text-red-600 font-bold">MAX {s.referral_credits_high ?? 0}</span>
                    {s.discount_low_pct || s.discount_high_pct ? (
                      <span className="text-gray-400">
                        {" "}
                        ({s.discount_low_pct ?? 0}%–{s.discount_high_pct ?? 0}% off)
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && (
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm text-gray-700">Company locked credits (closed months)</span>
              <span className="text-sm font-bold text-[#B45309]" title="All-time company credits locked from this promoter">
                {companyHist.total} total
              </span>
            </div>
            {companyHist.months.length === 0 ? (
              <div className="text-xs text-gray-400 pb-2">No locked months yet (locks on the 1st of each month).</div>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                <div className="flex justify-between text-[10px] uppercase text-gray-400 border-b pb-1">
                  <span className="w-20">Month</span>
                  <span className="flex-1 text-right">Live</span>
                  <span className="flex-1 text-right">Fetched</span>
                  <span className="flex-1 text-right">Company</span>
                </div>
                {companyHist.months.map((m) => (
                  <div key={m.month} className="flex justify-between items-center border-b py-1 text-xs">
                    <span className="w-20 text-gray-700">{m.month}</span>
                    <span className="flex-1 text-right text-[#5907FF]">{m.live_total}</span>
                    <span className="flex-1 text-right text-gray-500">{m.fetched_credits}</span>
                    <span className="flex-1 text-right text-[#B45309] font-bold">{m.company_credits}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LivePayoutDetailPopup;
